import {otsBaseApi} from "../ots-api";
import {
  AppAccessCreatePackage,
  AppAccessCapabilities,
  AppAccessDeliveryAudit,
  AppAccessEmailRequest,
  AppAccessIdentity,
  AppAccessLink,
  AppAccessSmsRequest,
  AppAccessPackage,
  AppAccessPackagesQuery,
} from "../../model/app-access";

export const appAccessApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ["AppAccessPackages", "AppAccessAudits"] as const,
}).injectEndpoints({
  endpoints: (builder) => ({
    getAppAccessCapabilities: builder.query<AppAccessCapabilities, void>({
      query: () => "/app-access/capabilities",
    }),
    getAppAccessIdentities: builder.query<AppAccessIdentity[], string>({
      query: (regionId) => ({url: "/app-access/identities", params: {regionId}}),
    }),
    getAppAccessPackages: builder.query<AppAccessPackage[], AppAccessPackagesQuery>({
      query: (request) => ({
        url: "/app-access/packages",
        params: {limit: request.limit ?? 50, regionId: request.regionId},
      }),
      providesTags: [{type: "AppAccessPackages", id: "LIST"}],
    }),
    createAppAccessPackage: builder.mutation<AppAccessPackage, AppAccessCreatePackage>({
      query: (body) => ({url: "/app-access/packages", method: "POST", body}),
      invalidatesTags: [{type: "AppAccessPackages", id: "LIST"}],
    }),
    revokeAppAccessPackage: builder.mutation<void, string>({
      query: (packageId) => ({url: `/app-access/packages/${packageId}/revoke`, method: "POST"}),
      invalidatesTags: (_result, _error, id) => [
        {type: "AppAccessPackages", id: "LIST"},
        {type: "AppAccessAudits", id},
      ],
    }),
    revokeAppAccessTarget: builder.mutation<void, {packageId: string; targetId: string}>({
      query: ({targetId}) => ({url: `/app-access/targets/${targetId}/revoke`, method: "POST"}),
      invalidatesTags: (_result, _error, {packageId}) => [
        {type: "AppAccessPackages", id: "LIST"},
        {type: "AppAccessAudits", id: packageId},
      ],
    }),
    emailAppAccessPackage: builder.mutation<void, {packageId: string; request: AppAccessEmailRequest}>({
      query: ({packageId, request}) => ({
        url: `/app-access/packages/${packageId}/email`, method: "POST", body: request,
      }),
      invalidatesTags: (_result, _error, {packageId}) => [{type: "AppAccessAudits", id: packageId}],
    }),
    smsAppAccessPackage: builder.mutation<void, {packageId: string; request: AppAccessSmsRequest}>({
      query: ({packageId, request}) => ({
        url: `/app-access/packages/${packageId}/sms`, method: "POST", body: request,
      }),
      invalidatesTags: (_result, _error, {packageId}) => [{type: "AppAccessAudits", id: packageId}],
    }),
    getAppAccessLink: builder.mutation<AppAccessLink, {packageId: string; targetId: string}>({
      query: ({targetId}) => ({url: `/app-access/targets/${targetId}/link`, method: "POST"}),
      invalidatesTags: (_result, _error, {packageId}) => [{type: "AppAccessAudits", id: packageId}],
    }),
    getAppAccessDeliveryAudits: builder.query<AppAccessDeliveryAudit[], string>({
      query: (packageId) => `/app-access/packages/${packageId}/delivery-audits`,
      providesTags: (_result, _error, id) => [{type: "AppAccessAudits", id}],
    }),
    getAppAccessPdf: builder.query<Blob, string>({
      query: (packageId) => ({
        url: `/app-access/packages/${packageId}/pdf`,
        headers: {Accept: "application/pdf"},
        responseHandler: (response) => response.blob(),
      }),
      async onQueryStarted(packageId, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;
          dispatch(appAccessApi.util.invalidateTags([{type: "AppAccessAudits", id: packageId}]));
        } catch (_error) {
          // Query state reports the error to the caller.
        }
      },
    }),
    getAppAccessQr: builder.query<Blob, {packageId: string; targetId: string}>({
      query: ({targetId}) => ({
        url: `/app-access/targets/${targetId}/qr`,
        headers: {Accept: "image/png"},
        responseHandler: (response) => response.blob(),
      }),
      async onQueryStarted({packageId}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;
          dispatch(appAccessApi.util.invalidateTags([{type: "AppAccessAudits", id: packageId}]));
        } catch (_error) {
          // Query state reports the error to the caller.
        }
      },
    }),
  }),
});

export const {
  useGetAppAccessCapabilitiesQuery,
  useGetAppAccessIdentitiesQuery,
  useGetAppAccessPackagesQuery,
  useCreateAppAccessPackageMutation,
  useRevokeAppAccessPackageMutation,
  useRevokeAppAccessTargetMutation,
  useEmailAppAccessPackageMutation,
  useSmsAppAccessPackageMutation,
  useGetAppAccessLinkMutation,
  useGetAppAccessDeliveryAuditsQuery,
  useLazyGetAppAccessPdfQuery,
  useLazyGetAppAccessQrQuery,
} = appAccessApi;
